var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
import { defineComponent, toRefs, ref, computed, createVNode, h, isVNode } from "vue";
import Textarea from "../textarea/index.js";
import Input from "../input/index.js";
import Trigger from "../trigger/index.js";
import Dropdown from "../_components/dropdown/dropdown.js";
import DropDownOption from "../_components/dropdown/dropdown-option.js";
import "../_components/dropdown/dropdown-optgroup.js";
import { useOptions } from "../_hooks/use-options.js";
import { getTextBeforeSelection, getLastMeasureIndex, isValidSearch } from "./utils.js";
import { getKeyDownHandler, CODE } from "../_utils/keyboard.js";
function _isSlot(s) {
  return typeof s === "function" || Object.prototype.toString.call(s) === "[object Object]" && !isVNode(s);
}
var _Mention = defineComponent({
  name: "Mention",
  inheritAttrs: false,
  props: {
    modelValue: String,
    defaultValue: {
      type: String,
      default: ""
    },
    data: {
      type: Array,
      default: () => []
    },
    prefix: {
      type: [String, Array],
      default: "@"
    },
    split: {
      type: String,
      default: " "
    },
    type: {
      type: String,
      default: "input"
    },
    onChange: {
      type: [Function, Array]
    },
    onSelect: {
      type: [Function, Array]
    }
  },
  emits: [
    "update:modelValue",
    "change",
    "search",
    "select"
  ],
  setup(props, {
    emit,
    attrs
  }) {
    const {
      data
    } = toRefs(props);
    const dropdownRef = ref();
    const optionRefs = ref({});
    const _value = ref(props.defaultValue);
    const computeValue = computed(() => {
      var _a;
      return (_a = props.modelValue) != null ? _a : _value.value;
    });
    const measureInfo = ref({
      measuring: false,
      location: -1,
      prefix: "",
      text: ""
    });
    const resetMeasureInfo = () => {
      measureInfo.value = {
        measuring: false,
        location: -1,
        prefix: "",
        text: ""
      };
    };
    const textareaRef = ref();
    const measureText = computed(() => measureInfo.value.text);
    const filterOption = ref(true);
    const handleInput = (value, e) => {
      const text = getTextBeforeSelection(e.target);
      const lastMeasure = getLastMeasureIndex(text, props.prefix);
      if (lastMeasure.location > -1) {
        const measureText2 = text.slice(lastMeasure.location + lastMeasure.prefix.length);
        if (isValidSearch(measureText2, props.split)) {
          measureInfo.value = __spreadValues({
            measuring: true,
            text: measureText2
          }, lastMeasure);
        }
        emit("search", measureText2);
      } else if (measureInfo.value.location > -1) {
        resetMeasureInfo();
      }
      _value.value = value;
      emit("update:modelValue", value);
      emit("change", value);
    };
    const _popupVisible = ref(false);
    const computedPopupVisible = computed(() => _popupVisible.value && measureInfo.value.measuring && optionNodes.value.length > 0);
    const handlePopupVisibleChange = (popupVisible) => {
      _popupVisible.value = popupVisible;
    };
    const {
      optionNodes,
      activeOption,
      getNextActiveOption,
      scrollIntoView,
      enabledOptionSet,
      optionInfoMap
    } = useOptions({
      options: data,
      inputValue: measureText,
      filterOption,
      dropdownRef,
      optionRefs
    });
    const handleSelect = (value, e) => {
      const measureStart = measureInfo.value.location;
      const measureEnd = measureInfo.value.location + measureInfo.value.text.length;
      let head = _value.value.slice(0, measureStart);
      let tail = _value.value.slice(measureEnd + 1);
      head += !head || head.endsWith(props.split) || head.endsWith("\n") ? "" : props.split;
      tail = (!tail || tail.startsWith(props.split) || tail.startsWith("\n") ? "" : props.split) + tail;
      const match = `${measureInfo.value.prefix}${value}`;
      const nextValue = `${head}${match}${tail}`;
      _value.value = nextValue;
      emit("select", value);
      emit("update:modelValue", value);
      emit("change", nextValue);
      resetMeasureInfo();
    };
    const handleMouseEnter = (value, e) => {
      const optionInfo = optionInfoMap.get(value);
      if (optionInfo) {
        activeOption.value = optionInfo;
      }
    };
    const handleMouseLeave = (e) => {
      activeOption.value = void 0;
    };
    const handleKeyDown = getKeyDownHandler(new Map([[CODE.ENTER, (e) => {
      if (computedPopupVisible.value) {
        if (activeOption.value) {
          handleSelect(activeOption.value.value);
        }
        e.preventDefault();
      }
    }], [CODE.ESC, (e) => {
      handlePopupVisibleChange(false);
      e.preventDefault();
    }], [CODE.ARROW_DOWN, (e) => {
      if (computedPopupVisible.value) {
        const next = getNextActiveOption("down");
        if (next) {
          activeOption.value = next;
          scrollIntoView(next.value);
        }
        e.preventDefault();
      }
    }], [CODE.ARROW_UP, (e) => {
      if (computedPopupVisible.value) {
        const next = getNextActiveOption("up");
        if (next) {
          activeOption.value = next;
          scrollIntoView(next.value);
        }
        e.preventDefault();
      }
    }]]));
    const renderOption = (item) => {
      const {
        value = ""
      } = item;
      return createVNode(DropDownOption, {
        ref: (ref2) => {
          if (ref2 == null ? void 0 : ref2.$el) {
            optionRefs.value[value] = ref2.$el;
          }
        },
        key: item.key,
        value: item.value,
        disabled: item.disabled,
        isActive: activeOption.value && value === activeOption.value.value,
        onClick: handleSelect,
        onMouseenter: handleMouseEnter,
        onMouseleave: handleMouseLeave
      }, {
        default: () => item.label
      });
    };
    const renderDropdown = () => {
      if (!measureInfo.value.measuring || optionNodes.value.length === 0) {
        return null;
      }
      const _children = optionNodes.value.map((node) => renderOption(node));
      return createVNode(Dropdown, {
        "ref": dropdownRef
      }, _isSlot(_children) ? _children : {
        default: () => [_children]
      });
    };
    const render = () => createVNode(Trigger, {
      "trigger": "focus",
      "position": "bl",
      "popupOffset": 4,
      "preventFocus": true,
      "popupVisible": computedPopupVisible.value,
      "clickToClose": false,
      "autoFitPopupWidth": true,
      "onPopupVisibleChange": handlePopupVisibleChange
    }, {
      default: () => [h(props.type === "textarea" ? Textarea : Input, __spreadValues({
        ref: textareaRef,
        modelValue: computeValue.value,
        onInput: handleInput,
        onKeydown: handleKeyDown
      }, attrs))],
      content: renderDropdown
    });
    return render;
  }
});
export { _Mention as default };
